const REGIONS = ["Construction Projects"];
const SITE_NAMES = [];
const SITE_OBJECTS = [];

const DEPARTMENTS = [
  "HSE",
  "Site Management",
  "Civil Works",
  "MEP",
  "Scaffolding",
  "Equipment",
  "Stores",
  "Internal Audit",
];

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const KPI_ROWS = [
  { key: "activeSites", name: "Active Construction Sites", group: "site" },
  { key: "equipmentTools", name: "Total Equipment & Power Tools", group: "equipment" },
  { key: "checklistsToday", name: "Checklists Completed Today", group: "checklists" },
  { key: "pendingInspections", name: "Pending Inspections", group: "inspections" },
  { key: "openFindings", name: "Open Findings / Observations", group: "findings" },
  { key: "actionsOpen", name: "Corrective Actions Open", group: "actions" },
  { key: "actionsClosed", name: "Corrective Actions Closed", group: "actions" },
  { key: "auditScore", name: "Monthly Internal Audit Score", group: "audit" },
  { key: "highRiskFindings", name: "High Risk Findings", group: "risk" },
  { key: "overdueActions", name: "Overdue Actions", group: "actions" },
  { key: "weeklyCompletion", name: "Weekly Inspection Completion %", group: "weekly" },
  { key: "dailyChecklistStatus", name: "Safety Officer Daily Checklist Status", group: "daily" },
  { key: "incidents", name: "Construction Incidents", group: "incident" },
  { key: "nearMiss", name: "Near Miss Reports", group: "incident" },
  { key: "inspections", name: "Weekly Site Inspections", group: "inspection" },
  { key: "internalAudit", name: "Monthly Internal Audits", group: "audit" },
  { key: "obsRaised", name: "Findings / Observations Raised", group: "findings" },
  { key: "obsClosed", name: "Findings / Observations Closed", group: "findings" },
  { key: "manpower", name: "Site Manpower", group: "people" },
  { key: "manhours", name: "Site Manhours", group: "people" },
  { key: "training", name: "HSE Trainings / Toolbox Talks", group: "training" },
  { key: "drills", name: "Emergency Drills", group: "training" },
  { key: "wasteGen", name: "Construction Waste Generated", group: "environment" },
  { key: "wasteRec", name: "Construction Waste Recycled", group: "environment" },
];

function trend(base, variance = 0.08, decimals = 0) {
  return MONTHS.map((_, i) => {
    const value = base * (1 + Math.sin((i + 1) / 2.4) * variance);
    return decimals ? +value.toFixed(decimals) : Math.round(value);
  });
}

function monthlyCounts(total, months) {
  const arr = MONTHS.map(() => 0);
  for (let i = 0; i < total; i += 1) arr[months[i % months.length] ?? i % 12] += 1;
  return arr;
}

function generateConstructionKPI() {
  const data = {};
  SITE_NAMES.forEach((site) => {
    data[site] = {};
    KPI_ROWS.forEach((row) => {
      data[site][row.key] = MONTHS.map(() => 0);
    });
  });
  return data;
}

const equipmentTools = [];

const checklistTemplates = [];

const dailyChecklists = [];

const inspections = [];

const observations = [];

const actions = [];

const audits = [];

const incidents = [];

const ncrs = [];

const ptws = [];

const trainings = [];

const waste = [];

const LEGAL_REFS = [
  { id: "LR-001", title: "ISO 45001 - Operational Planning and Control", regulation: "ISO 45001:2018", clause: "8.1", description: "Operational controls for construction HSE activities.", module: "All", status: "Active" },
  { id: "LR-002", title: "Scaffolding Inspection Requirements", regulation: "Construction HSE Standard", clause: "SCAF-01", description: "Scaffolds must be inspected and tagged before use.", module: "Inspection", status: "Active" },
  { id: "LR-003", title: "Working at Height Controls", regulation: "Construction HSE Standard", clause: "WAH-01", description: "Open edges and fall hazards require effective protection.", module: "Inspection", status: "Active" },
  { id: "LR-004", title: "Portable Equipment and Power Tool Safety", regulation: "Construction HSE Standard", clause: "EQP-01", description: "Portable equipment must be inspected, tagged and maintained.", module: "Equipment", status: "Active" },
];

const incTypes = ["Slip/Trip/Fall", "Struck By Object", "Electrical", "Working at Height", "Lifting", "Equipment / Power Tools", "Fire/Hot Work", "Vehicle Movement"];

export const SAMPLE = {
  schemaVersion: 8,
  sites: SITE_NAMES,
  departments: DEPARTMENTS,
  months: MONTHS,
  kpiRows: KPI_ROWS,
  monthlyKPI: generateConstructionKPI(),
  equipmentTools,
  checklistTemplates,
  dailyChecklists,
  incidents,
  inspections,
  actions,
  ptws,
  trainings,
  observations,
  ncrs,
  audits,
  waste,
  legalRefs: LEGAL_REFS,
  futureD1Tables: [
    "users",
    "roles",
    "sites",
    "equipment_tools",
    "checklist_templates",
    "checklist_items",
    "checklist_submissions",
    "audit_records",
    "findings_observations",
    "corrective_actions",
    "settings_master_data",
  ],
  settings: {
    company: "MWY Construction HSE & Internal Audit System",
    client: "Muhammad Waqas Younas",
    demoType: "Construction HSE & Internal Audit",
    sites: SITE_OBJECTS,
    departments: DEPARTMENTS,
    contractors: ["MWY Construction Team", "Civil Subcontractor", "MEP Subcontractor", "Scaffold Team", "Equipment Supplier"],
    equipmentCategories: ["Grinder", "Drill Machine", "Welding Machine", "Ladder", "Scaffolding", "Generator", "Compressor", "Electrical Tools", "Lifting Accessories"],
    checklistTypes: ["Equipment", "Daily", "Weekly", "Monthly Audit"],
    inspectionTypes: ["Equipment / Power Tools", "Safety Officer Daily Checklist", "Weekly Site Safety Inspection", "Monthly Internal Audit"],
    incidentTypes: incTypes,
    obsCategories: ["Housekeeping", "PPE", "Barricading", "Equipment", "Scaffolding", "Electrical Tools", "Working at Height", "Procedure"],
    sorCategories: ["Safe", "Unsafe", "Environmental", "Quality"],
    ncrCategories: ["Safety", "Process", "Documentation", "Equipment", "Scaffolding"],
    actionOwners: ["HSE Manager", "Safety Officer A", "Safety Officer B", "Site Manager A", "Site Manager B", "Civil Foreman", "MEP Supervisor", "Scaffold Supervisor", "Equipment Supervisor"],
    trainingTypes: ["Induction", "Toolbox Talk", "Equipment Specific", "Working at Height", "Scaffolding", "Emergency Drill"],
    ptwTypes: ["Hot Work", "Working at Height", "Electrical", "Excavation", "Lifting", "Confined Space"],
    auditTypes: ["Monthly Internal Audit"],
    riskLevels: ["Low", "Medium", "High", "Critical"],
    statusValues: ["Active", "Due", "Overdue", "Out of Service", "Open", "In Progress", "Closed", "Approved", "Expired", "Valid"],
    regions: REGIONS,
    users: [{ id: 1, name: "Muhammad Waqas Younas", role: "Administrator" }],
    targets: { weeklyInspectionCompletion: 95, dailyChecklistCompletion: 100, actionClosure: 90, auditScore: 90, highRiskClosureDays: 3 },
    themeColor: "#082B4D",
    address: "",
    contactPerson: "Muhammad Waqas Younas",
    email: "hse@mwy.example",
    phone: "+000 000 0000",
    reportHeader: "Muhammad Waqas Younas - Construction HSE & Internal Audit System",
    reportFooter: "Frontend demo data prepared for future Cloudflare Worker + D1 integration.",
  },
};

export { SITE_NAMES as SITES, SITE_OBJECTS, DEPARTMENTS, MONTHS, KPI_ROWS, REGIONS };





